import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { FeatureFlagService, PageKey } from '../../services/feature-flag.service';

/**
 * Structural directive to conditionally show/hide elements based on feature flags
 * 
 * Usage examples:
 * 
 * 1. Show content only if page has write access:
 *    <button *appFeatureFlag="'userManagement'">Edit User</button>
 * 
 * 2. Show content only if not in global read-only mode:
 *    <button *appFeatureFlag="null">Global Action</button>
 * 
 * 3. Show alternative content when write access is denied:
 *    <div *appFeatureFlag="'physicalTable'; else: readOnlyTemplate">
 *      <button>Edit Physical Table</button>
 *    </div>
 *    <ng-template #readOnlyTemplate>
 *      <p>Physical table is in read-only mode</p>
 *    </ng-template>
 * 
 * 4. Show content based on read-only mode:
 *    <div *appFeatureFlag="'roleManagement'; showWhenReadOnly: true">
 *      Read-only mode notice
 *    </div>
 */
@Directive({
  selector: '[appFeatureFlag]'
})
export class FeatureFlagDirective implements OnInit, OnDestroy {
  private hasView = false;

  @Input() appFeatureFlag!: PageKey | null;
  @Input() appFeatureFlagShowWhenReadOnly = false; // If true, shows content when read-only
  @Input() appFeatureFlagElse?: TemplateRef<any>;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.updateView();
  }

  ngOnDestroy() {
    this.viewContainer.clear();
  }

  private updateView() {
    const shouldShow = this.checkShouldShow();

    if (shouldShow && !this.hasView) {
      this.showMainContent();
    } else if (!shouldShow) {
      this.showAlternativeContent();
    }
  }

  private checkShouldShow(): boolean {
    if (this.appFeatureFlag === null) {
      // Check global read-only mode
      const isGlobalReadOnly = this.featureFlagService.isGlobalReadOnlyMode();
      return this.appFeatureFlagShowWhenReadOnly ? isGlobalReadOnly : !isGlobalReadOnly;
    }

    // Check page-specific read-only mode
    const isPageReadOnly = this.featureFlagService.isPageReadOnly(this.appFeatureFlag);
    return this.appFeatureFlagShowWhenReadOnly ? isPageReadOnly : !isPageReadOnly;
  }

  private showMainContent() {
    this.viewContainer.clear();
    this.viewContainer.createEmbeddedView(this.templateRef);
    this.hasView = true;
  }

  private showAlternativeContent() {
    this.viewContainer.clear();
    this.hasView = false;
    
    if (this.appFeatureFlagElse) {
      this.viewContainer.createEmbeddedView(this.appFeatureFlagElse);
    }
  }
}